/**
 * Проверки окна карточки площадки: все сразу, а не первая сработавшая.
 *
 * Раньше окно показывало одну полосу по принципу «первое сработавшее условие»:
 * про остальные проблемы человек не узнавал, пока не устранит первую. Теперь
 * считаются все семь проверок, а вывод («Что дальше») выбирает самую важную.
 * Отсутствие данных — не успех и не ошибка: проверка получает тон `none` и
 * причину.
 */

import type { NextStepChipTone, NextStepTone } from '../../../../components/ui/NextStepBar'
import type { CardWindowState } from './windowEditApi'
import {
  characteristicText,
  formatRub,
  plural,
  pluralFields,
  type McardCharacteristic,
  type McardModel,
} from './model'

export type McardTab =
  | 'overview'
  | 'text'
  | 'characteristics'
  | 'media'
  | 'price'
  | 'bundle'
  | 'history'

/** Наличие регистрации управления кабинетом, независимо от ревизии и состояния правки. */
export type LinkState = 'loading' | 'linked' | 'not_linked' | 'error'

/**
 * marketplace_cards регистрирует управление контентом в кабинете, отдельно от связи с товаром.
 */
export const NOT_CREATED_TITLE = 'Управление карточкой этого кабинета не зарегистрировано в ПИМ'
export const NOT_CREATED_HINT = 'Правка из окна станет доступна после регистрации управления карточкой кабинета.'
export const NOT_LINKED_TITLE = 'Карточка не связана с товаром ПИМ'

export const managementMissingHint = (designKnown: boolean): string =>
  (designKnown ? 'Товар и макет связаны с ПИМ' : 'Товар связан с ПИМ')
    + '; управление карточкой этого кабинета пока не зарегистрировано. ' + NOT_CREATED_HINT

export type McardPrimaryKind =
  | 'open_platform'
  | 'show_issues'
  | 'show_missing'
  | 'show_diff'
  | 'refresh_photos'
  | 'link_pim'

export interface McardCheck {
  key: string
  label: string
  value?: string
  tone: NextStepChipTone
  title?: string
  tab: McardTab
}

export interface McardVerdict {
  tone: NextStepTone
  title: string
  hint: string
  primary: McardPrimaryKind
  checks: McardCheck[]
}

/** Поля, по которым ПИМ и витрина расходятся (значение ПИМ с правками ≠ факт площадки). */
export const diffFields = (state: CardWindowState | null, modelFields: McardCharacteristic[] = []): string[] => {
  if (!state) return []
  const result: string[] = []
  const systemIds = new Set(modelFields.filter((item) => item.system).map((item) => item.targetId))
  const { title, description, media_urls: media, characteristics } = state.fields
  const same = (a: unknown, b: unknown) => characteristicText(a).trim() === characteristicText(b).trim()
  if (title.overridden && !same(title.value, title.platform_value)) result.push('Наименование')
  if (description.overridden && !same(description.value, description.platform_value)) result.push('Описание')
  if (media.overridden && !same(media.value, media.platform_value)) result.push('Фото')
  for (const item of characteristics) {
    if (!systemIds.has(item.target_id) && item.overridden && !same(item.value, item.platform_value)) result.push(item.name)
  }
  return result
}

export interface McardChecksInput {
  model: McardModel
  link: LinkState
  windowState: CardWindowState | null
  /** Состояние окна ещё грузится (карточка связана, ответа нет). */
  windowStateLoading: boolean
  /** Причина, по которой сравнения с ПИМ нет, если оно не получено. */
  windowStateWhy: string | null
  missingRequired: number
  /** Товар ПИМ найден (показан в колонке «Происхождение»), хотя карточки ПИМ нет. */
  productKnown?: boolean
  /** Макет тоже связан с ПИМ (товары без макета не требуют такой связи). */
  designKnown?: boolean
}

export function buildMcardVerdict(input: McardChecksInput): McardVerdict {
  const { model, link, windowState, windowStateLoading, windowStateWhy, missingRequired } = input
  const productKnown = input.productKnown === true
  const errors = model.issues.filter((issue) => issue.level === 'error')
  const warnings = model.issues.filter((issue) => issue.level === 'warning')
  const diff = diffFields(windowState, model.characteristics)
  const seoFlag = model.seoSyncState === 'conflict' || model.seoSyncState === 'local_ahead'

  // ── Чипы ──────────────────────────────────────────────────────────────────
  const checks: McardCheck[] = []

  checks.push({
    key: 'showcase',
    label: 'Витрина',
    value: model.showcase.value,
    tone: model.showcase.tone === 'ok' ? 'ok' : model.showcase.tone === 'warn' ? 'warn' : 'bad',
    tab: 'overview',
  })

  if (model.pimCheck) {
    checks.push({ key: 'pim', label: 'Совпадает с ПИМ', ...model.pimCheck, tab: 'text' })
  } else if (link === 'not_linked') {
    checks.push({ key: 'pim', label: 'Совпадает с ПИМ', value: productKnown ? 'управление не зарегистрировано' : 'не связана', tone: 'warn', tab: 'text', title: productKnown ? NOT_CREATED_TITLE : 'Карточка не связана с товаром ПИМ' })
  } else if (link === 'loading' || windowStateLoading) {
    checks.push({ key: 'pim', label: 'Совпадает с ПИМ', tone: 'none', tab: 'text', title: 'загружаем сравнение' })
  } else if (!windowState) {
    checks.push({ key: 'pim', label: 'Совпадает с ПИМ', tone: 'none', tab: 'text', title: windowStateWhy || 'сравнение с ПИМ недоступно' })
  } else if (diff.length > 0) {
    checks.push({ key: 'pim', label: 'Совпадает с ПИМ', value: `${diff.length} ${pluralFields(diff.length)}`, tone: 'warn', tab: 'text' })
  } else if (seoFlag) {
    checks.push({
      key: 'pim',
      label: 'Совпадает с ПИМ',
      value: model.seoSyncState === 'conflict' ? 'контент разошёлся' : 'есть неопубликованные правки',
      tone: 'warn',
      tab: 'text',
    })
  } else {
    checks.push({ key: 'pim', label: 'Совпадает с ПИМ', value: 'да', tone: 'ok', tab: 'text' })
  }

  checks.push({
    key: 'photos',
    label: 'Фото',
    value: String(model.photos.length),
    tone: model.photos.length > 0 ? 'ok' : 'bad',
    tab: 'media',
  })

  if (model.characteristicsCheck) {
    checks.push({ key: 'characteristics', label: 'Характеристики', ...model.characteristicsCheck, tab: 'characteristics' })
  } else if (!model.specsKnown) {
    checks.push({
      key: 'characteristics',
      label: 'Характеристики',
      tone: 'none',
      tab: 'characteristics',
      title: 'справочник характеристик предмета не загружен, обязательность неизвестна',
    })
  } else if (missingRequired > 0) {
    checks.push({
      key: 'characteristics',
      label: 'Характеристики',
      value: `${missingRequired} не заполнено`,
      tone: 'bad',
      tab: 'characteristics',
    })
  } else {
    checks.push({ key: 'characteristics', label: 'Характеристики', value: 'обязательные заполнены', tone: 'ok', tab: 'characteristics' })
  }

  checks.push(
    model.price.shown !== null && model.price.shown !== undefined
      ? { key: 'price', label: 'Цена', value: formatRub(model.price.shown), tone: 'info', tab: 'price', title: 'только показ: цена меняется в разделе «Цены»' }
      : { key: 'price', label: 'Цена', tone: 'none', tab: 'price', title: 'площадка не прислала цену' },
  )

  checks.push(
    model.stock !== null && model.stock !== undefined
      ? { key: 'stock', label: 'Остаток', value: `${model.stock.toLocaleString('ru-RU')} шт.`, tone: 'info', tab: 'price' }
      : { key: 'stock', label: 'Остаток', tone: 'none', tab: 'price', title: model.stockWhy || 'нет источника' },
  )

  checks.push(
    errors.length > 0
      ? { key: 'moderation', label: 'Модерация и ошибки', value: `${errors.length} ${plural(errors.length, ['ошибка', 'ошибки', 'ошибок'])}`, tone: 'bad', tab: 'overview' }
      : warnings.length > 0
        ? { key: 'moderation', label: 'Модерация и ошибки', value: `${warnings.length} ${plural(warnings.length, ['предупреждение', 'предупреждения', 'предупреждений'])}`, tone: 'warn', tab: 'overview' }
        : { key: 'moderation', label: 'Модерация и ошибки', value: 'нет', tone: 'ok', tab: 'overview' },
  )

  // ── Вывод: самая важная проблема ──────────────────────────────────────────
  const withChecks = (verdict: Omit<McardVerdict, 'checks'>): McardVerdict => ({ ...verdict, checks })

  if (model.showcase.tone !== 'ok' && model.showcase.problem) {
    return withChecks({
      tone: model.showcase.tone === 'warn' ? 'warn' : 'bad',
      title: model.showcase.problem.title,
      hint: model.showcase.problem.hint,
      primary: 'open_platform',
    })
  }
  if (errors.length > 0) {
    return withChecks({
      tone: 'bad',
      title: `Площадка вернула ${errors.length} ${plural(errors.length, ['ошибку', 'ошибки', 'ошибок'])} по карточке`,
      hint: warnings.length
        ? `Пока они не устранены, площадка не обновит карточку. Ещё ${warnings.length} ${plural(warnings.length, ['предупреждение', 'предупреждения', 'предупреждений'])}.`
        : 'Пока они не устранены, площадка не обновит карточку по нашим данным.',
      primary: 'show_issues',
    })
  }
  if (missingRequired > 0) {
    return withChecks({
      tone: 'bad',
      title: `Не заполнено ${missingRequired} обязательных характеристик`,
      hint: 'По данным зеркала обязательные поля пустые. Проверьте значения и правила категории перед отправкой; это не подтверждённый отказ площадки.',
      primary: 'show_missing',
    })
  }
  if (link === 'not_linked') {
    return withChecks({
      tone: 'info',
      title: productKnown ? NOT_CREATED_TITLE : 'Карточка не связана с товаром ПИМ',
      hint: productKnown
        ? managementMissingHint(input.designKnown === true)
        : 'Без связи она не получает контент и цены из ПИМ, а правка из окна недоступна.',
      primary: 'link_pim',
    })
  }
  if (diff.length > 0) {
    return withChecks({
      tone: 'warn',
      title: `Витрина расходится с ПИМ: ${diff.length} ${pluralFields(diff.length)}`,
      hint: `${diff.slice(0, 4).join(', ')}${diff.length > 4 ? ' и другие' : ''}. Правка сохранена в ПИМ, но на витрине ещё старое значение.`,
      primary: 'show_diff',
    })
  }
  if (seoFlag) {
    return withChecks({
      tone: 'warn',
      title: model.seoSyncState === 'conflict' ? 'Контент в ПИМ и на витрине разошёлся' : 'В ПИМ есть неопубликованные правки контента',
      hint: 'Откройте вкладку «Текст», чтобы сравнить значения.',
      primary: 'show_diff',
    })
  }
  if (model.photos.length === 0) {
    return withChecks({
      tone: 'bad',
      title: 'На витрине нет ни одной фотографии',
      hint: 'Карточка показывается покупателю пустым местом.',
      primary: 'refresh_photos',
    })
  }
  return withChecks({
    tone: 'ok',
    title: 'Карточка на витрине, расхождений не найдено',
    hint: model.lastSyncAt ? 'Данные сверены с площадкой.' : 'Дата последней сверки с площадкой неизвестна.',
    primary: 'open_platform',
  })
}
